/** @format */

import {useMemo} from 'react';

import DOMPurify from 'dompurify';
import {marked} from 'marked';

import {Box} from '@mui/material';
import {Trans} from 'react-i18next';

import type {AiAgentChatMessageRole, AiAgentChatMessageStatus} from 'graphql/generated/types';

import {parseContent} from './parseContent';
import ChartBlock from './ChartBlock';
import {UserBubble, AssistantBubble, FailedBubble} from './styles';

interface MessageBubbleProps {
  role: AiAgentChatMessageRole;
  content: string;
  status?: AiAgentChatMessageStatus;
}

const MessageBubble = ({role, content, status = 'COMPLETED'}: MessageBubbleProps) => {
  // Парсим/санитайзим markdown только для отображаемого ответа ассистента
  // (COMPLETED). Для USER (рендерит raw content), PENDING (null) и FAILED
  // (фикс. текст) результат не читается — не тратим marked.parse + DOMPurify.
  const renderedBlocks = useMemo(() => {
    if (role === 'USER' || status !== 'COMPLETED') return [];
    return parseContent(content).map(block =>
      block.type === 'chart'
        ? block
        : {...block, html: DOMPurify.sanitize(marked.parse(block.content) as string)},
    );
  }, [content, role, status]);

  if (role === 'USER') {
    return <UserBubble>{content}</UserBubble>;
  }

  if (status === 'PENDING') {
    return null;
  }

  if (status === 'FAILED') {
    return (
      <FailedBubble>
        <Trans>Не удалось получить ответ. Попробуйте ещё раз.</Trans>
      </FailedBubble>
    );
  }

  return (
    <AssistantBubble>
      {renderedBlocks.map((block, index) => {
        if (block.type === 'chart') {
          return <ChartBlock key={index} config={block.config} />;
        }

        return <Box key={index} dangerouslySetInnerHTML={{__html: block.html}} />;
      })}
    </AssistantBubble>
  );
};

export default MessageBubble;
